import { useCallback, useEffect, useMemo, useState } from "react";
import { Box, Breadcrumbs, Button, CircularProgress, Dialog, DialogActions, DialogContent, DialogTitle, IconButton, Typography } from "@mui/material";
import { DialogProps } from "@mui/material/Dialog";
import CloseIcon from "@mui/icons-material/Close";
import NavigateNextIcon from "@mui/icons-material/NavigateNext";
import { useStyles } from "./admin-registration.style";
import { RegistrationInformationStep } from "./steps/registration-information/registration-information.step";
import { ExamPaperSelectionStep } from "./steps/exam-paper-selection/exam-paper-selection.step";
import { ExamPaperSelectionItem } from "./steps/exam-paper-selection/use-exam-paper-selection.hook";
import { PreviewConfirmStep } from "./steps/preview-confirm/preview-confirm.step";
import { RegistrantSelectionStep } from "./steps/registrant-selection/registrant-selection.step";
import { RegisterExamRequest, SearchRegistrationExamineeResponseItem } from 'src/app/modules/Recruitment/types/license-exam-types';
import { AlertType, appendAlertItem, useTranslation } from "@pruforce/common-adminweb-sdk";
import { ExamSessionOption } from "./steps/registration-information/exam-session-selection-dialog/use-exam-session-selection.hook";
import { FileItem } from "./steps/registration-information/file-upload-panel/file-upload-panel.hook";
import { useRegistrationInformationValidation } from "./steps/registration-information/registration-information.validation";
import { registerExam } from "../../../network/license-exam-crud";
import { useDispatch } from "react-redux";

export interface AdminRegistrationDialogProps {
  open: boolean;
  onClose: (refresh?: boolean) => void;
}

export interface RegistrationInformation extends Omit<RegisterExamRequest, 'evidenceIds'> {
  evidenceFiles: FileItem[];
}
export interface AdminRegistrationFormData {
  selectedExamPaper: ExamPaperSelectionItem | null;
  selectedRegistrant: SearchRegistrationExamineeResponseItem | null;
  selectedVenue: string;
  selectedSession: ExamSessionOption | null;
  registrationInformation: RegistrationInformation
}

const TRANSLATION_KEY_PREFIX = 'recruitment.exam.registration.adminRegistration.registrationConfirmation.';
const IDENTITY_DOCUMENT_TYPE_BY_HKID: Record<string, string> = {
  Y: 'HKID',
  N: 'Passport',
};
const REGISTRATION_CONFIRMATION_ERROR_MAP: Record<string, string> = {
  duplicate_registration_not_allowed: 'duplicateRegistrationNotAllowed',
  exam_schedule_not_found: 'examScheduleNotFound',
  candidate_not_found: 'candidateNotFound',
  'Agent not found!': 'agentNotFound',
};

enum AdminRegistrationStep {
  ExamPaperSelection = 0,
  RegistrantSelection = 1,
  RegistrationInformation = 2,
  PreviewConfirm = 3,
}

const INITIAL_ACTIVE_STEP = AdminRegistrationStep.ExamPaperSelection;
const LAST_STEP_INDEX = AdminRegistrationStep.PreviewConfirm;
const getAdminRegistrationSteps = (): AdminRegistrationStep[] => {
  return Object.values(AdminRegistrationStep)
    .filter((value): value is AdminRegistrationStep => typeof value === 'number')
    .sort((left, right) => left - right);
};

const STEP_INDEXES = getAdminRegistrationSteps();

export const INITIAL_REGISTER_EXAM_REQUEST: RegistrationInformation = {
  examineeId: '',
  examScheduleId: '',

  firstname: '',
  lastname: '',
  localName: '',
  noChineseName: false,
  identityDocumentType: '',
  identityDocumentNumber: '',
  gender: 'M',
  birthday: '',
  countryCode: '+852',
  phoneNumber: '',
  email: '',
  evidenceFiles: [],
};

const INITIAL_FORM_DATA: AdminRegistrationFormData = {
  selectedExamPaper: null,
  selectedRegistrant: null,
  selectedVenue: '',
  selectedSession: null,
  registrationInformation: INITIAL_REGISTER_EXAM_REQUEST,
};

export const AdminRegistrationDialog = (props: AdminRegistrationDialogProps) => {
  const dispatch = useDispatch();
  const t = useTranslation();
  const { classes, cx } = useStyles();
  const { open, onClose } = props;
  const [activeStep, setActiveStep] = useState<AdminRegistrationStep>(INITIAL_ACTIVE_STEP);
  const [activatedSteps, setActivatedSteps] = useState<Set<AdminRegistrationStep>>(() => new Set([INITIAL_ACTIVE_STEP]));
  const validationSteps = new Set([AdminRegistrationStep.RegistrationInformation]);
  const [formData, setFormData] = useState<AdminRegistrationFormData>(INITIAL_FORM_DATA);
  const { errors, validate, validateField, clearError } = useRegistrationInformationValidation(formData);
  const [submitting, setSubmitting] = useState(false);
  const [confirmError, setConfirmError] = useState<string>();

  useEffect(() => {
    if (open) {
      return;
    }

    // Reset immediately when dialog closes, so next open starts clean on first render.
    setActiveStep(INITIAL_ACTIVE_STEP);
    setActivatedSteps(new Set([INITIAL_ACTIVE_STEP]));
    setFormData(INITIAL_FORM_DATA);

    setSubmitting(false);
    setConfirmError(undefined);

    clearError();
  }, [open, clearError]);

  useEffect(() => {
    if (!open) {
      return;
    }

    setActivatedSteps((prev) => {
      if (prev.has(activeStep)) {
        return prev;
      }
      const next = new Set(prev);
      next.add(activeStep);
      return next;
    });
  }, [activeStep]);

  const handleCancel = () => {
    onClose();
  };

  const handleBack = useCallback(() => {
    setActiveStep((prevStep) => Math.max(prevStep - 1, AdminRegistrationStep.ExamPaperSelection) as AdminRegistrationStep);
  }, []);

  const handleNext = useCallback(() => {
    if (validationSteps.has(activeStep) && !validate()) {
      return;
    }
    setActiveStep((prevStep) => Math.min(prevStep + 1, LAST_STEP_INDEX) as AdminRegistrationStep);
  }, [activeStep, validate]);

  const updateFormData = useCallback((patch: Partial<AdminRegistrationFormData>) => {
    setFormData((prev) => ({
      ...prev,
      ...patch,
    }));
  }, []);

  const handleConfirm = useCallback(async () => {
    if (submitting) {
      return;
    }
    try {
      setSubmitting(true);
      setConfirmError(undefined);

      const { evidenceFiles, ...registrationInformation } =
        formData.registrationInformation;

      const payload: RegisterExamRequest = {
        ...registrationInformation,
        evidenceIds: evidenceFiles.flatMap(
          ({ blobId }) => (blobId ? [blobId] : []),
        ),
      };

      await registerExam(payload);
      dispatch(
        appendAlertItem([
          {
            severity: AlertType.SUCCESS,
            title: t('global.submit.success'),
            content: t('recruitment.exam.registration.adminRegistration.registrationSuccessfulDescription'),
          },
        ]),
      );
      onClose(true);
    } catch (error) {
      const errorCode = (error as { message?: string })?.message;
      const translationKey = REGISTRATION_CONFIRMATION_ERROR_MAP[errorCode ?? ''];
      setConfirmError(t(translationKey ? `${TRANSLATION_KEY_PREFIX}${translationKey}` : 'common.serviceError'));
    } finally {
      setSubmitting(false);
    }
  }, [submitting, formData, onClose, t, dispatch]);

  const handleCloseConfirmError = useCallback(() => {
    setConfirmError(undefined);
  }, []);

  const renderStepContent = useCallback((step: AdminRegistrationStep) => {
    switch (step) {
      case AdminRegistrationStep.ExamPaperSelection:
        return (
          <ExamPaperSelectionStep
            value={formData.selectedExamPaper?.value || ''}
            onChange={(selectedExamPaper) =>
              updateFormData({
                selectedExamPaper,
                selectedRegistrant: null,
                registrationInformation: {
                  ...formData.registrationInformation,
                  examineeId: '',
                  examScheduleId: '',
                }
              })
            }
          />
        );
      case AdminRegistrationStep.RegistrantSelection:
        return (formData.selectedExamPaper &&
          <RegistrantSelectionStep
            selectedExamPaper={formData.selectedExamPaper}
            selectedRegistrantId={formData.selectedRegistrant?.examineeId || ''}
            onChange={(selectedRegistrant) =>
              updateFormData({
                selectedRegistrant,
                selectedVenue: '',
                selectedSession: null,
                registrationInformation: {
                  ...formData.registrationInformation,
                  evidenceFiles: [],
                  examineeId: selectedRegistrant?.examineeId || '',
                  firstname: selectedRegistrant?.firstname || '',
                  lastname: selectedRegistrant?.lastname || '',
                  localName: selectedRegistrant?.localName || '',
                  noChineseName: selectedRegistrant?.noChineseName || false,
                  identityDocumentType:
                    IDENTITY_DOCUMENT_TYPE_BY_HKID[selectedRegistrant?.hkId ?? ''] ?? '',
                  identityDocumentNumber: selectedRegistrant?.identityDocumentNumber || '',
                  gender: selectedRegistrant?.gender || '',
                  birthday: selectedRegistrant?.birthday || '',
                  countryCode: selectedRegistrant?.countryCode || '',
                  phoneNumber: selectedRegistrant?.phoneNumber || '',
                  email: selectedRegistrant?.email || '',
                },
              })}
          />
        );
      case AdminRegistrationStep.RegistrationInformation:
        return (
          <RegistrationInformationStep
            formData={formData}
            errors={errors}
            validateField={validateField}
            clearError={clearError}
            onChange={updateFormData} />
        );
      case AdminRegistrationStep.PreviewConfirm:
      default:
        return (
          <PreviewConfirmStep
            formData={formData}
            confirmError={confirmError}
            onDismissError={handleCloseConfirmError}
          />
        );
    }
  },
    [
      formData,
      updateFormData,
      validateField,
      clearError,
      errors,
      confirmError,
      handleCloseConfirmError,
    ]
  );

  const actionConfig = useMemo<Record<AdminRegistrationStep, {
    titleKey: string;
    leftLabel: string;
    leftAction: () => void;
    rightLabel: string;
    rightAction: () => void | Promise<void>;
    rightDisabled: boolean;
    maxWidth: DialogProps['maxWidth'];
  }>>(() => {
    const commonFollowUpConfig = {
      leftLabel: 'app.button.back',
      leftAction: handleBack,
      maxWidth: 'lg' as DialogProps['maxWidth'],
    };

    return {
      [AdminRegistrationStep.ExamPaperSelection]: {
        titleKey: 'recruitment.exam.registration.adminRegistration.step1Title',
        leftLabel: 'app.button.cancel',
        leftAction: handleCancel,
        rightLabel: 'app.button.next',
        rightAction: handleNext,
        rightDisabled: !formData.selectedExamPaper?.examCode,
        maxWidth: 'md' as DialogProps['maxWidth'],
      },
      [AdminRegistrationStep.RegistrantSelection]: {
        titleKey: 'recruitment.exam.registration.adminRegistration.step2Title',
        ...commonFollowUpConfig,
        rightLabel: 'app.button.next',
        rightAction: handleNext,
        rightDisabled: !formData.selectedRegistrant,
      },
      [AdminRegistrationStep.RegistrationInformation]: {
        titleKey: 'recruitment.exam.registration.adminRegistration.step3Title',
        ...commonFollowUpConfig,
        rightLabel: 'app.button.next',
        rightAction: handleNext,
        rightDisabled: false,
      },
      [AdminRegistrationStep.PreviewConfirm]: {
        titleKey: 'recruitment.exam.registration.adminRegistration.step4Title',
        ...commonFollowUpConfig,
        rightLabel: 'app.button.confirm',
        rightAction: handleConfirm,
        rightDisabled: false,
      },
    };
  }, [
    formData.selectedExamPaper?.examCode,
    formData.selectedRegistrant,
    handleBack,
    handleCancel,
    handleConfirm,
    handleNext
  ]);

  const currentAction = actionConfig[activeStep];
  const currentStepTitle = t(currentAction.titleKey);
  const currentStepTitleParts = useMemo<string[]>(() => {
    return currentStepTitle.split(" > ");
  }, [currentStepTitle]);

  const breadcrumbs = useMemo(() => {
    return currentStepTitleParts.map((titlePart: string, index: number) => (
      <Typography
        key={`${titlePart}-${index}`}
        className={classes.breadcrumbTitle}
        variant="h6"
        component="span"
      >
        {titlePart}
      </Typography>
    ));
  }, [currentStepTitleParts]);

  return (
    <div>
      <Dialog
        open={open}
        onClose={(_, reason) => {
          if (reason === 'backdropClick') {
            return;
          }
          onClose();
        }}
        maxWidth={currentAction.maxWidth}
        fullWidth
        PaperProps={{ className: classes.dialogPaper }}
      >
        <DialogTitle className={classes.dialogTitle}>
          <Box className={classes.dialogTitleContainer}>
            <Breadcrumbs
              className={classes.breadcrumbTitle}
              separator={<NavigateNextIcon fontSize="small" className={classes.breadcrumbSeparatorIcon} />}
              aria-label="breadcrumb"
            >
              {breadcrumbs}
            </Breadcrumbs>
            <IconButton className={classes.closeButton} onClick={() => onClose()} aria-label="close dialog" size="small">
              <CloseIcon fontSize="small" />
            </IconButton>
          </Box>
        </DialogTitle>
        <DialogContent dividers={true}>
          {STEP_INDEXES.map((step) => (
            activatedSteps.has(step)
              ? (
                <div key={step} hidden={activeStep !== step}>
                  {renderStepContent(step)}
                </div>
              )
              : null
          ))}
        </DialogContent>
        <DialogActions
          className={cx(
            classes.dialogActions,
            activeStep === AdminRegistrationStep.ExamPaperSelection
              ? classes.dialogActionsStep0
              : classes.dialogActionsOther,
          )}
        >
          <Button
            className={classes.actionButton}
            variant="outlined"
            disabled={submitting}
            onClick={currentAction.leftAction}
          >
            {t(currentAction.leftLabel)}
          </Button>

          <Button
            className={classes.actionButton}
            variant="contained"
            color="secondary"
            onClick={currentAction.rightAction}
            disabled={currentAction.rightDisabled || submitting}
          >
            {t(currentAction.rightLabel)}
            {submitting ? <CircularProgress color="inherit" sx={{ ml: 1 }} size={16} /> : null}
          </Button>
        </DialogActions>
      </Dialog>
    </div>
  );
};